import { useState } from 'react';
import { useGetCeoDebtsPortfolioQuery, type CeoDebtScheduleItem } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { DebtDetailModal } from '../DebtDetailModal';
import { TreasuryViewNote, useTreasuryWrite } from '../treasuryAccess';
import { HeaderActions } from '../ui';
import { AddDebtModal } from './AddDebtModal';
import { DebtsCalendarGrid } from './DebtsCalendarGrid';
import { DebtsImportDialog } from './DebtsImportDialog';
import { DebtsKpiStrip } from './DebtsKpiStrip';
import { DebtInstrumentsTable } from './DebtInstrumentsTable';
import { MarkPaidDialog } from './MarkPaidDialog';
import {
  ErrorRow,
  HeaderRow,
  RatesSubtext,
  Section,
  Select,
  Title,
} from './styles';
import type { DebtsPageProps } from './types';
import { UnmarkPaidDialog } from './UnmarkPaidDialog';
import { UpcomingScheduleTable } from './UpcomingScheduleTable';

export function CeoDebtsView({ editable = false }: DebtsPageProps) {
  const can_write = useTreasuryWrite(editable);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const { data, isLoading, isError, refetch } = useGetCeoDebtsPortfolioQuery(
    typeFilter ? { instrument_type: typeFilter } : undefined,
  );

  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  // Отметка оплаты и её снятие — те же окна, что в карточке договора.
  const [markItem, setMarkItem] = useState<CeoDebtScheduleItem | null>(null);
  const [unmarkItem, setUnmarkItem] = useState<CeoDebtScheduleItem | null>(null);

  if (isLoading) {
    return <Card>Загрузка портфеля и календаря кредитов и лизингов…</Card>;
  }

  if (isError || !data) {
    return (
      <Card>
        <ErrorRow>
          <span>Не удалось загрузить портфель кредитов и лизингов.</span>
          <Button $size="sm" $variant="secondary" onClick={() => refetch()}>
            Повторить
          </Button>
        </ErrorRow>
      </Card>
    );
  }

  return (
    <>
      {!editable && <TreasuryViewNote section="Кредиты и лизинг" to="/agents/finance/debts" />}
      <Section>
        <HeaderRow>
          <div>
            <Title>Календарь и портфель кредитов, ВКЛ/НКЛ, лизингов и банковских гарантий</Title>
            <RatesSubtext>
              Ключевая ставка ЦБ РФ: <strong>{data.cbr_key_rate_pct.toFixed(1)}%</strong> ·
              Средневзвешенная ставка портфеля:{' '}
              <strong>{data.weighted_avg_rate_pct.toFixed(2)}%</strong> · Покрытие выплат 30 дн.
              свободным остатком: <strong>{data.cash_coverage_ratio_30d.toFixed(1)}x</strong>
            </RatesSubtext>
          </div>

          <HeaderActions>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="Какие договоры показать"
            >
              <option value="">Все инструменты (Кредиты + Лизинг + БГ)</option>
              <option value="credits">Только кредиты и кредитные линии (ВКЛ/НКЛ)</option>
              <option value="leasing">Только лизинг спецтехники и автотранспорта</option>
              <option value="bank_guarantee">Только банковские гарантии (БГ)</option>
            </Select>
            {can_write && (
              <>
                <Button $size="sm" onClick={() => setShowAddModal((v) => !v)}>
                  + Добавить кредит / лизинг
                </Button>
                <Button $size="sm" $variant="secondary" onClick={() => setShowImport(true)}>
                  Загрузить из Excel
                </Button>
              </>
            )}
          </HeaderActions>
        </HeaderRow>

        {can_write && showAddModal && (
          <AddDebtModal
            onCreated={(id) => {
              setShowAddModal(false);
              setSelectedInstrumentId(id);
            }}
            onClose={() => setShowAddModal(false)}
          />
        )}

        <DebtsKpiStrip
          totalCreditPrincipal={data.total_credit_principal}
          availableCreditLimit={data.available_credit_limit}
          totalLeasingPrincipal={data.total_leasing_principal}
          leasedUnitsTotal={data.leased_units_total}
          totalGuaranteesAmount={data.total_guarantees_amount}
          paymentsNext30d={data.payments_next_30d}
          paymentsNext7d={data.payments_next_7d}
        />
      </Section>

      <DebtsCalendarGrid calendarByMonth={data.calendar_by_month} />

      <DebtInstrumentsTable
        instruments={data.instruments}
        onSelectInstrument={setSelectedInstrumentId}
      />

      <UpcomingScheduleTable
        schedule={data.upcoming_schedule}
        can_write={can_write}
        onSelectInstrument={setSelectedInstrumentId}
        onMarkPaid={setMarkItem}
        onUnmarkPaid={setUnmarkItem}
      />

      {can_write && markItem && (
        <MarkPaidDialog item={markItem} onClose={() => setMarkItem(null)} />
      )}
      {can_write && unmarkItem && (
        <UnmarkPaidDialog item={unmarkItem} onClose={() => setUnmarkItem(null)} />
      )}

      {selectedInstrumentId && (
        <DebtDetailModal
          instrumentId={selectedInstrumentId}
          onClose={() => setSelectedInstrumentId(null)}
          editable={editable}
        />
      )}

      {can_write && (
        <DebtsImportDialog open={showImport} onClose={() => setShowImport(false)} />
      )}
    </>
  );
}
